import { useMemo, useState } from 'react';

import PrimaryProviderAssignmentPanel from '@/components/PrimaryProviderAssignmentPanel';
import emptySelectOption from '@/constants/emptySelectOption';
import usePrimaryProviderAssignment from '@/hooks/usePrimaryProviderAssignment';
import { AppointmentType } from '@/types/appointmentType';
import Select from '@/ui/components/Form/Select';
import Modal from '@/ui/components/Modal';
import formatStaffName from '@/utils/formatStaffName';

import { ProxyCreateAppointmentVariables, TimeSlot } from './types';

interface ConfirmationModalProps {
  slot: TimeSlot;
  open: boolean;
  appointmentType: AppointmentType;
  timeZone: string;
  defaultProviderId: number;
  onClose: () => void;
  onSubmit: (variables: ProxyCreateAppointmentVariables) => void;
  isPending: boolean;
}

const ConfirmationModal = ({
  onClose,
  open,
  onSubmit,
  isPending: createPending,
  slot,
  appointmentType,
  timeZone,
  defaultProviderId
}: ConfirmationModalProps) => {
  const [selectedProvider, setSelectedProvider] = useState<number>(defaultProviderId);
  const [setPrimaryProvider, setSetPrimaryProvider] = useState<boolean>(false);

  const relationshipType = appointmentType.relationship_type;

  const selectedOption = useMemo(
    () => slot.providers.find((p) => p.healthie_id === selectedProvider),
    [slot, selectedProvider]
  );

  const {
    canSetPrimary,
    currentRelationships,
    currentStaffNames,
    isSelectedProviderCurrent,
    tooManyPrimaries
  } = usePrimaryProviderAssignment(relationshipType, selectedOption?.id ?? '');

  const textContent = () => {
    const parsedDate = new Date(slot.time);
    const day = parsedDate.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone
    });
    const time = parsedDate.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone
    });
    const shortTimezone = parsedDate.toLocaleTimeString(undefined, {
      timeZoneName: 'short',
      timeZone
    });

    const selectedProviderName = selectedOption ? formatStaffName({ staff: selectedOption }) : '';

    return (
      <div className="">
        <div className="flex flex-col justify-center text-center">
          <p className="font-bold mb-8">{appointmentType.name}</p>
          <p className="mb-1">{day}</p>
          <p className="mb-1 font-bold text-xl">{time}</p>
          <p className="mb-4">{`${timeZone.replaceAll('_', ' ')} ${shortTimezone.split(' ').at(2)}`}</p>
        </div>
        <Select
          label="Provider"
          placeholder="Select a provider"
          options={slot.providers.map((provider) => ({
            label: `${provider.first_name || ''} ${provider.last_name || ''}`.trim(),
            value: provider.healthie_id
          }))}
          onChange={(option) => {
            setSelectedProvider(Number(option.value));
            setSetPrimaryProvider(false);
          }}
          selectedOption={
            selectedOption
              ? {
                  value: selectedOption.healthie_id,
                  label:
                    `${selectedOption.first_name || ''} ${selectedOption.last_name || ''}`.trim()
                }
              : emptySelectOption
          }
        />
        {relationshipType && (
          <PrimaryProviderAssignmentPanel
            canSetPrimary={canSetPrimary}
            checked={setPrimaryProvider}
            currentRelationshipsCount={currentRelationships.length}
            currentStaffNames={currentStaffNames}
            isSelectedProviderCurrent={isSelectedProviderCurrent}
            onCheckedChange={setSetPrimaryProvider}
            relationshipType={relationshipType}
            selectedProviderName={selectedProviderName}
            tooManyPrimaries={tooManyPrimaries}
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Confirm Appointment Creation"
      textContent={textContent()}
      blurredBackground
      button={{
        children: 'Create Appointment',
        title: 'Confirm Booking',
        ariaLabel: 'Create Appointment',
        disabled: createPending,
        loading: createPending,
        onClick: () => {
          onSubmit({
            appointment_type_id: appointmentType.healthie_id.toString(),
            datetime: slot.time,
            staff_healthie_id: selectedProvider.toString(),
            ...(relationshipType ? { set_primary_provider: setPrimaryProvider } : {})
          });
        }
      }}
    />
  );
};

export default ConfirmationModal;
